import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import type { AuditEntry } from './schoolApi';

const ACTIONS: Record<string, string> = {
  'staff.added': 'Added to staff',
  'staff.role_changed': 'Role changed',
  'staff.removed': 'Access removed',
  'staff.restored': 'Access given again',
  'class.created': 'Class added',
  'class.renamed': 'Class renamed',
  'class.teachers_changed': 'Class teachers changed',
  'class.deleted': 'Class deleted',
  'pupil.added': 'Pupil added',
  'pupil.renamed': 'Pupil renamed',
  'pupil.classes_changed': 'Pupil\'s classes changed',
  'pupil.removed': 'Pupil removed',
  'school.renamed': 'School renamed',
  'ai.turned_on': 'AI and outside experts allowed',
  'ai.turned_off': 'AI and outside experts turned off',
  'parents_two_factor.required': 'Two-step sign-in required for parents',
  'parents_two_factor.optional': 'Two-step sign-in made optional for parents',
  'logo.changed': 'Logo changed',
  'logo.removed': 'Logo removed',
  'report.downloaded': 'Progress spreadsheet downloaded',
  'school.exported': 'All school data downloaded',
  'school.pupil_data_erased': 'All pupil data deleted',
};

export default function AuditTab() {
  const t = useT();
  const [rows, setRows] = useState<AuditEntry[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (before?: number) => {
    try {
      const page = await api<AuditEntry[]>(`/school/audit${before ? `?before=${before}` : ''}`, { token: true });
      setRows((r) => (before && r ? [...r, ...page] : page));
      setMore(page.length === 100);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load the audit log.'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="card">
      <h3>{t('Audit log')}</h3>
      <p className="field-hint" style={{ marginTop: 0 }}>{t('Every change made in the school admin area, and every download, newest first. Entries cannot be changed or deleted.')}</p>
      {error && <div className="error" role="alert">{error}</div>}
      {rows === null ? (
        <p className="muted">{t('Loading…')}</p>
      ) : rows.length === 0 ? (
        <p className="muted">{t('Nothing has been recorded yet.')}</p>
      ) : (
        <div className="table-wrap">
          <table className="report-table">
            <thead>
              <tr>
                <th scope="col">{t('When')}</th>
                <th scope="col">{t('Who')}</th>
                <th scope="col">{t('What')}</th>
                <th scope="col">{t('Details')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.at).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>{r.actor ?? '–'}</td>
                  <td>{t(ACTIONS[r.action] ?? r.action)}</td>
                  <td>{[r.subject, r.detail].filter(Boolean).join(' — ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {more && rows && (
        <div className="toolbar">
          <button type="button" className="btn ghost" onClick={() => void load(rows[rows.length - 1].id)}>{t('Show older entries')}</button>
        </div>
      )}
    </div>
  );
}
